import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Encoding } from '@mode-vegas/vegas';
import {
  ChartTypes,
  DataViewResponseTypes,
  FeatureFlag,
  ReportDomain,
  ReportTypes,
} from '@mode/shared/contract-common';
import { getInitialFlagValue, MetaTags, MetaTagsService } from '@mode/shared/util-js';
import { FeatureFlagsFacade } from '@mode/shared/contract-common';
import { ApiService, TEMPLATE_PARSER, TemplateParser } from '@mode/shared/util-halogen';
import { ObservabilityEvents, ObservabilityService } from '@mode/shared/util-observability';
import { ChartResource } from 'libs/shared/contract-common/src/lib/charts';
import { Includes } from 'libs/shared/contract-common/src/lib/mode-api';
import { DataView, ImportedDatasetWithShareLink } from 'libs/shared/contract-common/src/lib/switch/switch-view.types';
import { difference } from 'lodash';
import { Observable, forkJoin, from, of, throwError } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import {
  ReportResponseTypes,
  SchedulesResource,
  normalizeSwitchView,
  switchViewResponseToDataView,
  toImportDatasetResult,
} from '../..';
import {
  toDbtMetadata,
  toReport,
  toReportParameterOptions,
  toReportWithDatasetResponse,
} from '../data-mappers/report-data-mappers';
import { QUERY_RUN_RESOURCE_PROPERTIES, QueryRunResource } from '../query-runs/query-run-response.types';
import { chartCache, queryRunCache, reportCache, switchViewCache } from '../resource-cache';
import {
  ReportDbtMetaDataResponse,
  ReportResource,
  ReportRunFormFieldsOptionsResponse,
  ReportViewerVisualizationsResponse,
} from './report-response.types';
import { PRODUCER_RUN_RESOURCE_PROPERTIES } from '../data-runs/data-run-response.types';
import { toSchedules } from '../data-mappers/account-data-mappers';

@Injectable({ providedIn: 'root' })
export class ReportClientService {
  private readonly _reportEntry = this.urlTemplateService.parse('/api/{username}/reports/{token}');
  publishLocalDatasetEnabled = getInitialFlagValue(FeatureFlag.NotebookPublishExportedDatasets);

  constructor(
    private http: HttpClient,
    private apiService: ApiService,
    private featureFlagsFacade: FeatureFlagsFacade,
    private metaTags: MetaTagsService,
    private observabilityService: ObservabilityService,
    @Inject(TEMPLATE_PARSER) private urlTemplateService: TemplateParser
  ) {}

  public loadNewReport(report: ReportResponseTypes.ReportResource) {
    // Currently new report creation is handled in the legacy app
    // so this is just a pass through client call. In the future there should
    // be a report client request for a new report resource from the BE here
    return of(report).pipe(
      tap((resource) => reportCache.set(report.token, resource)),
      map((resource) => toReport(resource))
    );
  }

  public loadSimpleReport(username: string, token: string) {
    const cached = reportCache.get(token);
    if (!cached) {
      return from(
        this.apiService.getFromPath<ReportResource>(this._reportEntry.expand({ username, token }), {
          includes: ['report_theme', 'display_properties', 'creator', 'space'],
        })
      ).pipe(
        tap((resource) => reportCache.set(token, resource)),
        map((resource) => toReport(resource))
      );
    } else {
      return of(toReport(cached));
    }
  }

  public loadHeaderReportData(token: string, includes: string[]): Observable<ReportResource> {
    return from(
      this.apiService.getFromPath<ReportResource>(this._reportEntry.expand({ username: this.getOrg(), token }), {
        includes,
      })
    );
  }

  /* The API /reports is restricted when the report loads with a user signed out.
      We are able to use /reports/.../runs/ and ask for an embeded report object.
      To keep the API from overfetching, 'only' is being
      used to not have the api return run specific data.
  */
  public loadRestrictedHeaderReportData(): Observable<ReportResource> {
    const entryPath = this.getReportRunEntryPath();
    return from(
      this.http.get<ReportResource>(entryPath, {
        params: { ['only']: '', 'embed[report]': 1 },
      })
    ).pipe(map((response) => response?._embedded?.['report'] as ReportResource));
  }

  public saveReport(token: string, name: string, description: string) {
    const cachedReport = reportCache.get(token);
    if (cachedReport?._forms) {
      return from(
        this.apiService.submit<ReportResource>(cachedReport._forms['edit'], {
          report: {
            name,
            description,
            color_mappings: '{}',
            space_token: '',
          },
        })
      ).pipe(
        tap((resource) => reportCache.set(token, resource)),
        map((resource) => toReport(resource))
      );
    } else {
      return throwError(new Error('No Report in cache to rename'));
    }
  }

  /**
   *
   * Glorious Future - Report
   *
   * This endpoint loads only the report "shell." This represents metadata from the report as well as other data that
   * makes sense to be returned to us with the report.
   */
  public loadReport(accountId: string, reportToken: string) {
    const urlPath = `/internal/${accountId}/reports/${reportToken}`;
    const urlParams = `embed[space]=1&embed[creator]=1&embed[report_theme]=1&embed[filters]=1`;
    const requestUrl = `${urlPath}?${urlParams}`;
    return this.http.get<ReportResponseTypes.ReportResponse>(requestUrl);
  }

  /**
   * Load all data needed to dispatch the initial report.
   *
   * This helper chains the 2 endpoints together returning a combined object that can be queried.
   */
  public loadFullReport(accountId: string, reportToken: string, options: { runId: string | null } | null) {
    const reportContainer$ = this.loadReport(accountId, reportToken);
    const loadReportView$ = this.getLoadReportView(accountId, reportToken, options?.runId);

    this.observabilityService.mark(ObservabilityEvents.REPORT_RESOURCE_LOADING);
    return this.loadReportViewWithoutVizTokens(reportContainer$, loadReportView$);
  }

  private getLoadReportView(accountId: string, reportToken: string, runId: string | null | undefined) {
    return runId
      ? this.loadReportViewForReportRun(accountId, reportToken, runId)
      : this.loadReportViewMostRecentSuccessful(accountId, reportToken);
  }

  private loadReportViewWithoutVizTokens(
    reportContainer$: Observable<ReportResponseTypes.ReportData>,
    loadReportView$: Observable<ReportResponseTypes.ReportViewerLastSuccessfulRunResponse>
  ) {
    return forkJoin([reportContainer$, loadReportView$]).pipe(
      map(([reportContainerResult, reportViewerResult]) => ({ reportContainerResult, reportViewerResult })),
      tap(() => this.endReportLoadingObservability())
    );
  }

  private endReportLoadingObservability() {
    this.observabilityService.mark(ObservabilityEvents.REPORT_RESOURCE_LOADED);
    this.observabilityService.recordSpanTiming(
      ObservabilityEvents.REPORT_RESOURCE_LOADED,
      ObservabilityEvents.REPORT_RESOURCE_LOADING
    );
  }

  loadReportView(accountId: string, reportToken: string, reportRunToken?: string) {
    const reportView$ = reportRunToken
      ? this.loadReportViewForReportRun(accountId, reportToken, reportRunToken)
      : this.loadReportViewMostRecentSuccessful(accountId, reportToken);
    return from(reportView$).pipe(
      tap((resource) => {
        /* optionally cache report view resource */
      }),
      map((resource) => resource)
    );
  }

  /**
   * Glorious Future - Report View
   *
   * These two endpoints represents all of the data we need to actually render the report (ie - visualizations & data views).
   * This is separate from the `loadReport` endpoint because the FE is currently responsible for parsing the layout
   * and providing the viz tokens that are needed.
   *
   * These endpoints load the visualizations requested in additional to all of the switch views contained in the report.
   *
   * Additionally, there are two separate endpoints, one that takes a reportRunToken and one that does not.
   */
  public loadReportViewMostRecentSuccessful(accountId: string, reportToken: string) {
    const request = `/api/${accountId}/report_views/${reportToken}`;

    const producerRunFilteredFields = difference(PRODUCER_RUN_RESOURCE_PROPERTIES, [
      'raw_source',
      'rendered_source',
    ]).toString();

    const params: { [key: string]: string | string[] } = {
      'embed[local_switch_views][switch_views][switch_attrs][switch_attrs]': '1',
      'embed[local_switch_views][switch_views][last_successful_data_run][creator]': '1',
      'embed[local_switch_views][switch_views][last_successful_data_run][result]': '1',
      'embed[local_switch_views][switch_views][last_successful_data_run][producer_run][only]':
        producerRunFilteredFields,
      'embed[imported_switch_views][switch_views][switch_attrs][switch_attrs]': '1',
      'embed[imported_switch_views][switch_views][last_successful_data_run][creator]': '1',
      'embed[imported_switch_views][switch_views][last_successful_data_run][result]': '1',
      'embed[imported_switch_views][switch_views][last_successful_data_run][producer_run][only]':
        producerRunFilteredFields,
      'embed[report_run]': '1',
      account_id: accountId,
      report_id: reportToken,
    };

    return this.http.get<ReportResponseTypes.ReportViewerLastSuccessfulRunResponse>(request, { params });
  }

  public loadReportViewForReportRun(accountId: string, reportToken: string, reportRunToken: string) {
    const request = `/api/${accountId}/report_views/${reportToken}`;

    const producerRunFilteredFields = difference(PRODUCER_RUN_RESOURCE_PROPERTIES, [
      'raw_source',
      'rendered_source',
    ]).toString();

    const params: { [key: string]: string | string[] } = {
      'embed[local_switch_views][switch_views][switch_attrs][switch_attrs]': '1',
      'embed[local_switch_views][switch_views][data_run_for_run_in_context][creator]': '1',
      'embed[local_switch_views][switch_views][data_run_for_run_in_context][result]': '1',
      'embed[local_switch_views][switch_views][data_run_for_run_in_context][producer_run][only]':
        producerRunFilteredFields,
      'embed[imported_switch_views][switch_views][switch_attrs][switch_attrs]': '1',
      'embed[imported_switch_views][switch_views][data_run_for_run_in_context][creator]': '1',
      'embed[imported_switch_views][switch_views][data_run_for_run_in_context][result]': '1',
      'embed[imported_switch_views][switch_views][data_run_for_run_in_context][producer_run][only]':
        producerRunFilteredFields,
      'embed[report_run]': '1',
      account_id: accountId,
      report_id: reportToken,
      report_run: reportRunToken,
    };

    return this.http.get<ReportResponseTypes.ReportViewerRunInContextResponse>(request, { params });
  }

  public importDataset(accountId: string, reportToken: string, queryToken: string) {
    const url = `/api/${accountId}/reports/${reportToken}/import_dataset`;

    const payload = {
      query_token: queryToken,
      account_id: accountId,
      id: reportToken,
    };

    return this.http
      .post<DataViewResponseTypes.ImportDatasetResult>(url, payload)
      .pipe(map((response) => toImportDatasetResult(response)));
  }

  public importDatasetWithChart(
    accountId: string,
    reportId: string,
    queryToken: string,
    chartColorPaletteToken: string,
    encoding: Encoding,
    format: ChartTypes.ChartFormat,
    fieldFormats: ChartTypes.FieldFormatMap
  ) {
    const url = `/api/${accountId}/reports/${reportId}/import_dataset`;

    const payload = {
      account_id: accountId,
      id: reportId,
      query_token: queryToken,
      chart: {
        color_palette_token: chartColorPaletteToken,
        view: {},
        view_version: 2,
        view_vegas: {
          encoding: encoding,
          format: format,
          chartType: 'table',
          title: '',
          description: '',
          caption: ChartTypes.EMPTY_CAPTION,
          fieldFormats: fieldFormats ?? {},
          fieldColors: {},
          facetSizes: {},
          isAutoUpdateDisabled: false,
        },
      },
    };

    return this.http
      .post<DataViewResponseTypes.ImportDatasetResult>(url, payload)
      .pipe(map((response) => toImportDatasetResult(response)));
  }

  /**
   * Temporary stopgap for getting the imported dataViews for the datasets project.
   *
   * The `imported` parameter will return all visualizations on all dataViews.
   */
  public loadDataViews(
    accountId: string,
    reportToken: string,
    switchViewToken: string
  ): Observable<ImportedDatasetWithShareLink> {
    const url = `/api/${accountId}/report_views/${reportToken}`;
    const params: { [key: string]: string } = {
      imported: 'true',
      'embed[imported_switch_views][switch_views][switch_attrs][switch_attrs]': '1',
      'embed[imported_switch_views][switch_views][visualizations][visualizations]': '1',
      'embed[imported_switch_views][switch_views][new_visualization]': '1',
      'embed[imported_switch_views][switch_views][last_data_run][result]': '1',
      'embed[imported_switch_views][switch_views][last_successful_data_run][creator]': '1',
      'embed[imported_switch_views][switch_views][last_successful_data_run][result]': '1',
      'embed[imported_switch_views][switch_views][last_successful_data_run][producer_run]': '1',
      'embed[report_run]': '1',
    };

    return from(this.http.get<ReportResponseTypes.ReportViewerLastSuccessfulRunResponse>(url, { params })).pipe(
      tap((response) => {
        response._embedded.imported_switch_views._embedded.switch_views.forEach((switchView) => {
          // TODO: This is a stopgap measure to use the new cache setup
          // with the legacy API response object. When this endpoint is
          // refactored to return the new Resource type, we won't
          // need to cast this response as any.
          switchViewCache.set(switchView.token, switchView as any);

          if (switchView.token === switchViewToken) {
            const chartResource = switchView._embedded?.visualizations?._embedded?.visualizations[0];
            if (chartResource) {
              chartCache.set(chartResource.token, chartResource as any);
            }
          }
        });
      }),
      map((response) => {
        const currentSwitchView =
          response._embedded.imported_switch_views._embedded.switch_views.find(
            (switchView) => switchView.token === switchViewToken
          ) ?? ({} as DataViewResponseTypes.SwitchViewWithRunsResponses);

        let importedDataset = {} as DataView;

        if (Object.keys(currentSwitchView).length) {
          const normalizedSwitchViews: DataViewResponseTypes.NormalizedSwitchViewResponse =
            normalizeSwitchView(currentSwitchView);

          importedDataset = switchViewResponseToDataView(normalizedSwitchViews);
        }

        const shareLink: string = (response._embedded.report_run._links as any)['share']['href'];

        return {
          importedDataset,
          shareLink,
          mostRecentReportRunToken: response.most_recent_report_run_token,
        };
      })
    );
  }

  public loadQueryRun(accountId: string, reportToken: string, reportRunToken: string, queryRunToken: string) {
    const cached = queryRunCache.get(queryRunToken);
    if (!cached) {
      const url = `/api/${accountId}/reports/${reportToken}/runs/${reportRunToken}/query_runs/${queryRunToken}`;

      const queryRunFilteredFields = difference(QUERY_RUN_RESOURCE_PROPERTIES, [
        'id',
        'max_result_bytes',
        'parameters',
        'query_created_at',
        'query_name',
        'statement_annotation',
        'token',
      ]).toString();

      const params: { [key: string]: string } = {
        '[only]': queryRunFilteredFields,
      };

      return this.http.get<QueryRunResource>(url, { params }).pipe(
        map((response) => {
          queryRunCache.set(queryRunToken, response);
          return response;
        })
      );
    } else {
      return of(cached);
    }
  }

  /**
   * Intended use from editor.
   *
   * It is assumed that local_switch_views are requested with `last_data_run` right now, which
   * makes this editor specific.
   */
  public getReportWithViews(accountId: string, reportToken: string) {
    const url = `/api/${accountId}/reports/${reportToken}`;
    const cached = reportCache.get(reportToken);

    const importedSwitchViewsInclude: Includes = {
      imported_switch_views: {
        switch_views: [
          'switch_attrs',
          'new_visualization',
          'visualizations',
          { last_data_run: ['result', 'producer_run', 'creator'] },
          { last_successful_data_run: ['result', 'producer_run', 'creator'] },
        ],
      },
    };

    const localSwitchViewsInclude: Includes = {
      local_switch_views: {
        switch_views: [
          'switch_attrs',
          'new_visualization',
          'visualizations',
          ...(this.publishLocalDatasetEnabled ? [{ query: ['thoughtspot_publisher'] }] : ['query']), // Need query for the temporary workaround with respect to filling unrun SwitchViews
          { last_data_run: ['creator', 'producer_run', 'result'] },
        ],
      },
    };

    let includes = [importedSwitchViewsInclude, localSwitchViewsInclude];

    return from(
      cached != null
        ? of(cached)
        : this.apiService.getFromPath<ReportResource>(url, {
            includes,
          })
    ).pipe(
      tap((resource) => reportCache.set(resource.token, resource)),
      map((resource) => toReport(resource))
    );
  }

  /**
   * Used to refresh the report data and its embeddings without the cache
   * It will not use the cache but will still hydrate the cache
   */
  public getReportWithViewsWithoutCache(accountId: string, reportToken: string) {
    const url = `/api/${accountId}/reports/${reportToken}`;

    const importedSwitchViewsInclude: Includes = {
      imported_switch_views: {
        switch_views: [
          'switch_attrs',
          'new_visualization',
          'visualizations',
          { last_data_run: ['result', 'producer_run', 'creator'] },
          { last_successful_data_run: ['result', 'producer_run', 'creator'] },
        ],
      },
    };

    const localSwitchViewsInclude: Includes = {
      local_switch_views: {
        switch_views: [
          'switch_attrs',
          'new_visualization',
          'visualizations',
          ...(this.publishLocalDatasetEnabled ? [{ query: ['thoughtspot_publisher'] }] : ['query']), // Need query for the temporary workaround with respect to filling unrun SwitchViews
          { last_data_run: ['creator', 'producer_run', 'result'] },
        ],
      },
    };

    const localDatasetsInclude: Includes = {
      local_datasets: {
        local_datasets: [
          'new_chart',
          {
            charts: {
              charts: ['color_palette'],
            },
          },
          'new_query_table',
          'query_tables',
        ],
      },
    };

    let includes = ['latest_report_run', importedSwitchViewsInclude, localSwitchViewsInclude, localDatasetsInclude];

    return from(
      this.apiService.getFromPath<ReportResource>(url, {
        includes,
      })
    ).pipe(
      tap((resource) => reportCache.set(resource.token, resource)),
      map((resource) => toReport(resource))
    );
  }

  /**
   * Intended use from dataset view page.
   * The datasetReport doesn't have an imported switchView,
   * and it needs the local switchview to have last_successful_data_run,
   * adding last_successful_data_run could cause issues in normal workflows
   */
  public getDatasetReportWithViews(accountId: string, reportToken: string) {
    const url = `/api/${accountId}/reports/${reportToken}`;
    const localSwitchViewsInclude: Includes = {
      local_switch_views: {
        switch_views: [
          'switch_attrs',
          'new_visualization',
          'visualizations',
          ...(this.publishLocalDatasetEnabled ? [{ query: ['thoughtspot_publisher'] }] : ['query']), // Need query for the temporary workaround with respect to filling unrun SwitchViews
          { last_data_run: ['creator', 'producer_run', 'result'] },
          { last_successful_data_run: ['creator', 'producer_run', 'result'] },
        ],
      },
    };

    let includes = [localSwitchViewsInclude, 'space', 'creator', 'thoughtspot_publisher'];

    return from(
      this.apiService.getFromPath<ReportResource>(url, {
        includes,
      })
    ).pipe(
      tap((resource) => reportCache.set(resource.token, resource)),
      map((resource) => toReport(resource))
    );
  }

  public getReportParameterOptions(
    accountUserName: string,
    reportId: string,
    runId: string
  ): Observable<ReportDomain.ReportRunFormFieldOptions[]> {
    const url = `api/${accountUserName}/reports/${reportId}/runs/${runId}/options`;
    return this.http.get<ReportRunFormFieldsOptionsResponse>(url).pipe(
      map((response) => {
        return response._embedded.form_fields.map((resource) => toReportParameterOptions(resource));
      })
    );
  }

  public getReportDbtMetadata(accountUserName: string, reportId: string): Observable<ReportTypes.ReportDbtMetaData> {
    const url = `api/${accountUserName}/reports/${reportId}/dbt_metadata`;
    return this.http.get<ReportDbtMetaDataResponse>(url).pipe(map((response) => toDbtMetadata(response)));
  }

  public createReportWithDataset(accountId: string, switchViewToken: string) {
    const url = `/api/${accountId}/reports`;

    const payload = {
      import_switch_view_token: switchViewToken,
    };

    return this.http.post<ReportResponseTypes.CreateReportWithDatasetResponse>(url, payload).pipe(
      map((response) => {
        return toReportWithDatasetResponse(response);
      })
    );
  }

  public createReportAndChartWithDataset(
    switchViewToken: string,
    chartColorPaletteToken: string,
    encoding: Encoding,
    format: ChartTypes.ChartFormat,
    fieldFormats: ChartTypes.FieldFormatMap
  ) {
    const url = `/api/${this.getOrg()}/reports`;

    const payload = {
      import_switch_view_token: switchViewToken,
      chart: {
        color_palette_token: chartColorPaletteToken,
        view: {},
        view_vegas: {
          encoding: encoding,
          format: format,
          chartType: 'table',
          title: '',
          description: '',
          caption: ChartTypes.EMPTY_CAPTION,
          fieldFormats: fieldFormats ?? {},
          fieldColors: {},
          facetSizes: {},
          isAutoUpdateDisabled: false,
        },
        view_version: 2,
      },
    };

    return this.http
      .post<ReportResponseTypes.CreateReportAndChartWithDatasetResponse>(url, payload)
      .pipe(map((response) => response));
  }

  private getOrg() {
    const orgId = this.metaTags.getTag(MetaTags.OwnerUsername);

    if (!orgId) {
      throw new Error('orgId missing from meta tags! It should be in the owner-username tag');
    }

    return orgId;
  }
  private getReportRunEntryPath() {
    const entryPath = this.metaTags.getTag(MetaTags.ReportRunEntryPath);

    if (!entryPath) {
      throw new Error('report run entry path missing from meta tags! It should be in the report-run-entry-path tag');
    }

    return entryPath;
  }

  public loadSchedules(reportId: string) {
    const url = `/api/${this.getOrg()}/reports/${reportId}/schedules`;
    return from(
      this.apiService.getFromPath<SchedulesResource>(url, {
        includes: ['report_schedules'],
      })
    ).pipe(map((response) => toSchedules(response, 'report_schedules')));
  }

  public loadReportViewVisualizations(accountId: string, reportToken: string) {
    const request = `/api/${accountId}/reports/${reportToken}/visualizations`;

    const params: { [key: string]: string | string[] } = {
      'embed[visualizations]': '1',
    };
    return this.http.get<ReportViewerVisualizationsResponse>(request, { params });
  }

  public loadExploreVisualization(accountId: string, reportToken: string, vizToken: string) {
    const request = `/api/${accountId}/visualizations/${vizToken}`;

    return this.http.get<ChartResource>(request);
  }

  public getClonedReportData(url: string): Observable<ReportResource> {
    return this.http.get<ReportResource>(url, {
      params: {
        'embed[creator]': 1,
        'embed[star]': 1,
        'embed[new_star]': 1,
        'embed[new_space_link]': 1,
        'embed[space][creator]': 1,
      },
    });
  }
}
